/*
 * <simple-carousel> custom element.
 *
 * Used by sections/featured-collection.liquid in "simple carousel" mode.
 * Wraps a horizontal scroll track of product cards and provides:
 *   - small dash-style pagination indicators (one dot per product in
 *     the original cycle, so dot clicks move by a single product),
 *   - click-and-drag swipe with the mouse,
 *   - optional seamless infinite loop when the markup renders the
 *     product list three times (data-loop="true"),
 *   - a small symmetric "peek" of the previous/next card on both
 *     edges, anchored via scroll-padding-left so the snap positions
 *     stay correctly offset.
 *
 * Important: this element does NOT rely on `scroll-behavior: smooth`
 * being set in CSS. Using the CSS value would animate the initial
 * scrollLeft assignment that places the viewport in the middle copy,
 * which produced a visible "scrolls through on refresh" flash. All
 * animated scrolls go through scrollTo({behavior:'smooth'}) explicitly.
 *
 * Required structure inside the element:
 *   .simple-carousel__track   - the scrollable container
 *   .simple-carousel__dots    - empty container we populate
 *
 * Optional host attributes:
 *   data-loop="true"
 *   data-original-count="<int>"
 *   data-autoscroll="true"        - slow continuous drift (see _initAutoScroll)
 *   data-autoscroll-speed="<px/s>"
 *   data-autoscroll-direction="left" | "right"  - which way the cards travel
 *   data-indicator="dashes" | "bar"  - what replaces the dash row when it would
 *                                   wrap (phones): a sliding window of dashes
 *                                   (default) or the single progress bar.
 *                                   Theme setting: Cards > carousel_indicator_style.
 *
 * The idle drift never scrolls the track. It translates an extra wrapper that
 * sits inside the track and holds the cards (see _initDriftLayer), so the motion
 * runs on the compositor while the track stays a normal scroll container at all
 * times — gestures latch onto it at any moment, including mid roll-out. The
 * translation is folded back into scrollLeft the instant anyone actually acts,
 * so drag, inertia, dots and the infinite loop never see it.
 */
// Several sections load this file (featured collection, category tabs ...):
// a second run must not declare the class again (SyntaxError in the console).
if (!window.__oaSimpleCarousel) {
window.__oaSimpleCarousel = true;
class SimpleCarousel extends HTMLElement {
  // Setting a carousel up measures the page (card widths, dot rows). During
  // the initial load every carousel did that at once, right when the parser
  // reached it - on the homepage three carousels behind the full-screen hero
  // cost ~0.75 s of blocked main thread at DOMContentLoaded (lab measure),
  // exactly while the hero logo fades in and the visitor starts to scroll.
  // Now: while the page is still loading, a carousel sets itself up when it
  // comes within ~1.5 screens of the viewport (IntersectionObserver - no
  // forced layout) or, at the latest, one per idle moment after "load".
  // Inserted later (tabs, sections re-rendered): immediately, as before.
  connectedCallback() {
    if (this._oaDone) return;
    if (document.readyState === 'complete' || !('IntersectionObserver' in window)) { this._oaInitNow(); return; }
    const C = SimpleCarousel;
    if (!this._oaIo) {
      this._oaIo = new IntersectionObserver((en) => {
        if (en.some((e) => e.isIntersecting)) this._oaInitNow();
      }, { rootMargin: '150% 0px 150% 0px' });
      this._oaIo.observe(this);
    }
    C._oaQueue = C._oaQueue || [];
    C._oaQueue.push(this);
    if (!C._oaQueued) {
      C._oaQueued = true;
      const idle = window.requestIdleCallback
        ? (fn) => window.requestIdleCallback(fn, { timeout: 1500 })
        : (fn) => setTimeout(fn, 120);
      const pump = () => {
        let c;
        while ((c = C._oaQueue.shift()) && c._oaDone);
        if (!c) return;
        c._oaInitNow();
        idle(pump);
      };
      window.addEventListener('load', () => idle(pump), { once: true });
    }
  }

  _oaInitNow() {
    if (this._oaDone || !this.isConnected) return;
    this._oaDone = true;
    if (this._oaIo) { this._oaIo.disconnect(); this._oaIo = null; }
    this._oaInit();
  }

  _oaInit() {
    this.track = this.querySelector('.simple-carousel__track');
    this.dotsContainer = this.querySelector('.simple-carousel__dots');
    if (!this.track || !this.dotsContainer) return;

    // Stop horizontal flings/drags from chaining to the page (and from
    // triggering the browser's back-swipe gesture) when the track reaches
    // an edge - that chaining is what causes a hitch at the ends.
    this.track.style.overscrollBehaviorX = 'contain';

    this.slides = this.track.querySelectorAll('[id^="Slide-"]');
    if (this.slides.length < 2) return;

    this.isLoop = this.dataset.loop === 'true';
    const parsedCount = parseInt(this.dataset.originalCount, 10);
    this.originalCount = Number.isFinite(parsedCount) && parsedCount > 0
      ? parsedCount
      : (this.isLoop ? Math.floor(this.slides.length / 3) : this.slides.length);

    // How many copies of the product list are rendered, and which copy is the
    // middle one we keep the viewport anchored to. Derived from the DOM so it
    // adapts if the copy count ever changes.
    this._copies = Math.max(1, Math.round(this.slides.length / this.originalCount));
    this._middleCopy = this.isLoop ? Math.floor(this._copies / 2) : 0;

    // Measure + position before announcing readiness, so the CSS
    // opacity gate stays at 0 until the carousel is already in its
    // final initial position. The very first painted frame the user
    // sees already shows the middle copy with symmetric peek - no
    // shake, no run-through.
    this._initDriftLayer();
    this._measure();
    this._positionToMiddle();
    this._renderDots();
    this._initScroll();
    this._initDrag();
    this._initDotsDrag();
    this._initResize();
    this._initAutoScroll();
    this._initWarmup();
    this._initPlaceholders();
    this.classList.add('simple-carousel--ready');
  }

  _snapSlides() {
    // Give every card a WHOLE-pixel pitch (card width + gap).
    //
    // The loop re-anchors by jumping exactly one copy of 25 cards. On a phone
    // the cards come out fractional (390px screen: 170.22px), so one copy is
    // 4555.47px — but a scroll position only takes whole (WebKit) or
    // device-pixel (Chrome) steps. The jump could never be exact: measured in
    // headless iPhone/Pixel emulation, EVERY re-anchor moved the picture by up
    // to 0.5–0.7px, i.e. two device pixels on a 3x screen, right after the swipe
    // came to rest. That was the remaining twitch. Rounding the card by at most
    // half a pixel makes every copy a whole number of pixels and the jump exact.
    //
    // Inline sizes are cleared first so the natural (CSS) width is measured
    // fresh on every resize; only called from _measure, never per frame.
    if (!this.isLoop) return;
    const props = ['flex', 'width', 'max-width'];
    this.slides.forEach((s) => props.forEach((k) => s.style.removeProperty(k)));
    const w = this.slides[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.slides[0].parentElement).columnGap) || 0;
    if (!(w > 0)) return;
    const snapped = Math.max(1, Math.round(w + gap) - gap);
    if (Math.abs(snapped - w) < 0.001) return;
    const px = snapped + 'px';
    this.slides.forEach((s) => {
      s.style.setProperty('flex', '0 0 ' + px);
      s.style.setProperty('width', px);
      s.style.setProperty('max-width', px);
    });
  }

  _initPlaceholders() {
    // The loading tile with the "opening act." watermark is pure CSS
    // (component-card.css); this only marks each card once its photo is in, so
    // the mark disappears and cannot show through a transparent image.
    this.slides.forEach((slide) => {
      const media = slide.querySelector('.card__media .media');
      const img = media && media.querySelector('img');
      if (!img) return;
      const done = () => media.classList.add('oa-shown');
      if (img.complete && img.naturalWidth > 0) done();
      else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  _initWarmup() {
    // Load and decode the product photos BEFORE the first swipe (phones).
    //
    // The photos are lazy: a card's image is only requested once the browser
    // sees it coming into view. On a freshly loaded page the first swipe
    // therefore did all of it at once — fetch, then decode, for every card it
    // revealed — and the decode lands on the same thread that paints. That was
    // the "first swipe after a reload stutters, photos pop in late" symptom;
    // the second pass was smooth because everything was cached by then.
    //
    // So we walk through one copy of the products in the order a swipe would
    // reveal them (two ahead, one behind, …), switch their images to eager and
    // decode them off the main thread (img.decode()). The other two copies
    // show the SAME files, so flipping them to eager only picks them up from
    // the memory cache.
    //
    // Phones/tablets only (< 990px): that is where the cards are swiped and the
    // images are small (533w); ~25 photos per carousel, a fraction of what the
    // collection grid once held (see oa-swipe-placeholder.js).
    if (!this.isLoop) return;
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    const n = this.originalCount;
    const pick = (slide) => slide
      && (slide.querySelector('.media--hover-effect > img') || slide.querySelector('.card__media img'));

    const order = [];
    const seen = new Set();
    for (let f = 0, b = n - 1; order.length < n;) {
      for (let j = 0; j < 2 && f < n; j++, f++) {
        if (!seen.has(f)) { seen.add(f); order.push(f); }
      }
      if (b >= 0) {
        if (!seen.has(b)) { seen.add(b); order.push(b); }
        b--;
      }
    }

    // Queue the work instead of doing it on approach. The first version
    // started when the carousel came within a screen of the viewport — which is
    // exactly while the visitor is scrolling down towards it, so the fetching
    // and decoding competed with that scroll and with the carousel's own reveal
    // animation (Clemens: "first scroll down stutters, especially as the
    // carousels fade in"). Now every carousel queues its photos as soon as the
    // page has loaded, into ONE shared queue (so they warm one after another,
    // top of the page first), throttled to one photo at a time while anything
    // scrolls or a finger is on the screen — see SimpleCarousel._warm.
    const enqueue = () => {
      order.forEach((i) => SimpleCarousel._warm(() => {
        for (let c = 0; c < this._copies; c++) {
          const im = pick(this.slides[c * n + i]);
          if (im && im.loading === 'lazy') im.loading = 'eager';
        }
        const main = pick(this.slides[this._middleCopy * n + i]);
        return main && main.decode ? main.decode() : null;
      }));
    };
    if (document.readyState === 'complete') enqueue();
    else window.addEventListener('load', enqueue, { once: true });
  }

  static _warm(task) {
    // One queue for all carousels on the page, top of the page first. Two
    // photos in flight at a time, only one within 250ms of any scroll (page or
    // carousel — a capturing listener on window sees both) or of a finger
    // touching or moving.
    const C = SimpleCarousel;
    if (!C._warmTasks) {
      C._warmTasks = [];
      C._warmActive = 0;
      C._warmTimer = null;
      C._lastActivity = performance.now();
      const mark = () => { C._lastActivity = performance.now(); };
      window.addEventListener('scroll', mark, { capture: true, passive: true });
      window.addEventListener('touchstart', mark, { capture: true, passive: true });
      window.addEventListener('touchmove', mark, { capture: true, passive: true });
    }
    C._warmTasks.push(task);
    C._warmSchedule(600); // let the page settle after load first
  }

  static _warmSchedule(ms) {
    const C = SimpleCarousel;
    if (C._warmTimer) return;
    C._warmTimer = setTimeout(() => { C._warmTimer = null; C._warmPump(); }, ms);
  }

  static _warmPump() {
    const C = SimpleCarousel;
    while (C._warmTasks.length) {
      // Two at a time when nothing moves; ONE while scrolling. Pausing
      // completely meant that someone who scrolled straight down reached the
      // lower carousel before a single one of its photos had been prepared —
      // it felt a touch rougher than the top one. (The fade-in stutter this
      // pause was meant for turned out to be the per-card GPU layers, fixed in
      // component-card.css.) A decode runs off the main thread, so one in
      // flight costs the scroll next to nothing.
      const quiet = performance.now() - C._lastActivity;
      const limit = quiet < 250 ? 1 : 2;
      if (C._warmActive >= limit) {
        if (limit === 1) C._warmSchedule(260 - quiet); // widen again once quiet
        return;
      }
      const task = C._warmTasks.shift();
      C._warmActive++;
      Promise.resolve().then(task).catch(() => {}).then(() => {
        C._warmActive--;
        C._warmPump();
      });
    }
  }

  _measure() {
    this._snapSlides();
    const first = this.slides[0];
    const second = this.slides[1];
    this.slideOffset = Math.max(1, second.offsetLeft - first.offsetLeft);
    // Cache every slide's offsetLeft ONCE per measure. _recenter() used to read
    // it live, i.e. inside the animation frame right after writing scrollLeft —
    // and a layout read straight after a layout write forces the browser to
    // recalculate the whole track then and there. Sixty times a second that is
    // exactly the stutter you feel. Re-measured on resize like everything else.
    this._slideOffsets = Array.prototype.map.call(this.slides, (s) => s.offsetLeft);
    // Width this measure was taken at (the resize observer skips ticks that do
    // not change it) and how far the track can scroll at all (the loop hand-off
    // only steps in when a fling is about to run out of track). Both cached
    // here so the per-frame scroll work never has to read layout.
    this._measuredW = this.track.clientWidth;
    this._maxScroll = Math.max(0, this.track.scrollWidth - this._measuredW);
    const fit = this.track.clientWidth / this.slideOffset;
    this.slidesPerView = Math.max(1, Math.floor(fit + 0.05));

    // Optional "centered single" mode. Below the configured viewport width we
    // force exactly one card per view so the adjacent cards render as large,
    // symmetric peeks (the mobile CSS sizes the card so each peek is ~3/5 of a
    // card). Without this, floor() above would treat the ~2.2 cards that fit as
    // two full cards and centre two instead of one.
    const centerMax = parseInt(this.dataset.centerSingleMax, 10);
    if (Number.isFinite(centerMax) && window.innerWidth <= centerMax) {
      this.slidesPerView = 1;
    }

    // Symmetric peek: split the spare viewport space between left and
    // right. We use the actually-measured slide width (first slide
    // bounding rect minus its left gap proxy = slideOffset minus
    // computed column-gap). For our use-case the difference between
    // "slide width" and "slide offset" is tiny so using slideOffset
    // here is close enough and avoids reading computed style every
    // resize.
    const sparePx = this.track.clientWidth - this.slidesPerView * this.slideOffset;
    this.peekOffset = Math.max(0, sparePx / 2);
    // The visible left/right peek would otherwise be asymmetric by one
    // column-gap (the right peek ends up one gap wider), because slideOffset
    // includes the gap but the row of full cards has one fewer gap than the
    // offset count assumes. Shifting the rest position by half a gap makes the
    // partial cards at both edges get cut equally.
    const cardWidth = first.getBoundingClientRect().width;
    this.gap = Math.max(0, this.slideOffset - cardWidth);
    this.restOffset = this.peekOffset + this.gap / 2;

    // EXACT width of one rendered copy, measured from the offset between the same
    // product in adjacent copies. This is NOT the same as originalCount*slideOffset:
    // sub-pixel rounding makes individual slide offsets vary (e.g. 268 vs 269), so
    // n*slideOffset can be a few px short of a true copy. Re-anchoring by this exact
    // value keeps the loop jump pixel-identical (invisible); using the approximation
    // produced a small visible "hard cut" each loop.
    this.copyWidth = (this.isLoop && this.slides.length > this.originalCount)
      ? (this.slides[this.originalCount].offsetLeft - this.slides[0].offsetLeft)
      : (this.originalCount * this.slideOffset);
    // peekOffset is applied to scrollLeft in _positionToMiddle and
    // _goToDot so the symmetric peek is visible at load-time and after
    // a dot click. With CSS scroll-snap disabled, free scrolling and
    // dragging will leave the carousel wherever the user releases it
    // - which is the requested behaviour.
  }

  _positionToMiddle() {
    if (!this.isLoop) return;
    // Direct assignment, no smooth scroll. Sets the viewport at the
    // first slide of the middle copy minus the peek offset so the
    // visual peek is symmetric from the very first frame.
    this.track.scrollLeft = (this._middleCopy * this.copyWidth) - this.restOffset;
    this._balancePeek();
  }

  _balancePeek() {
    // Fine-tune the scroll position so the partial cards peeking at the LEFT
    // and RIGHT edges are cut to the same width. The gap-based restOffset gets
    // it close; this measures the actual rendered peeks and removes any residual.
    //
    // Direction matters: leftPeek = (left card's right edge) - viewport.left, so
    // it SHRINKS as scrollLeft grows; rightPeek = viewport.right - (right card's
    // left edge), so it GROWS as scrollLeft grows. To equalise when the right peek
    // is larger (d = right - left > 0) we must shift content rightwards, i.e.
    // DECREASE scrollLeft by d/2 (and vice-versa). Using += here pushes away from
    // balance, and because the ResizeObserver re-invokes this every tick the error
    // compounds - that produced the ~one-gap skew we saw. The sign must be -=.
    const rect = this.track.getBoundingClientRect();
    let left = null;
    let right = null;
    for (let i = 0; i < this.slides.length; i++) {
      const b = this.slides[i].getBoundingClientRect();
      if (b.width < 5) continue;
      if (left === null && b.left < rect.left - 0.5 && b.right > rect.left + 0.5) {
        left = b.right - rect.left;
      }
      if (right === null && b.left < rect.right - 0.5 && b.right > rect.right + 0.5) {
        right = rect.right - b.left;
      }
    }
    if (left !== null && right !== null) {
      this.track.scrollLeft -= (right - left) / 2;
    }
  }

  _renderDots() {
    this.dotsContainer.innerHTML = '';
    this._progressThumb = null;
    // Reset the window mode BEFORE measuring: its fixed width / nowrap would
    // otherwise hide the wrap that the check below looks for.
    this._dotWindow = 0;
    this.dotsContainer.classList.remove('simple-carousel__dots--window');
    this.dotsContainer.style.removeProperty('--oa-dot-shift');
    this.dotsContainer.removeAttribute('data-oa-edge');
    // The dashes are rebuilt from scratch here, so the freshly created ones
    // carry no is-active class. _updateActiveDot() skips its work when the
    // index has not changed since last time — without clearing that memory the
    // skip applied to brand-new dashes, and the dark one only appeared once the
    // carousel had moved on by a whole product. (This runs again on every
    // resize tick, not just at startup.)
    this._lastDotIndex = null;
    const dotCount = this.originalCount;
    if (dotCount <= 1) {
      this.dotsContainer.hidden = true;
      return;
    }
    this.dotsContainer.hidden = false;

    const dots = [];
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'simple-carousel__dot';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', `Go to product ${i + 1} of ${dotCount}`);
      dot.addEventListener('click', () => this._goToDot(i));
      this.dotsContainer.appendChild(dot);
      dots.push(dot);
    }

    // If one dash per product wraps onto more than one row (many products on a
    // narrow/phone screen), swap the dashes for a single compact progress track
    // with a sliding thumb. It stays on one row for ANY product count and keeps
    // the same minimalist aesthetic: one dash gliding along a faint line. When
    // the dashes fit on one row we leave them as-is (and they remain clickable).
    const wrapped = dots[dots.length - 1].offsetTop > dots[0].offsetTop + 1;
    if (wrapped && this.dataset.indicator !== 'bar') {
      // Default: Instagram-style window (same as the product cards and the
      // product-page gallery). The dashes keep their size, at most DOT_WINDOW
      // (or as many as fit) are visible and the row slides with the active
      // product; CSS .simple-carousel__dots--window sizes/masks the row and
      // reads --oa-dot-shift. The dashes stay real buttons, so tap-to-jump,
      // hover and drag-to-scrub keep working (see _initDotsDrag).
      const pitch = dots[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.dotsContainer).columnGap) || 0);
      const cc = getComputedStyle(this.dotsContainer);
      const avail = this.dotsContainer.clientWidth - parseFloat(cc.paddingLeft) - parseFloat(cc.paddingRight);
      const fit = Math.max(3, Math.floor((avail + (pitch - dots[0].getBoundingClientRect().width)) / Math.max(1, pitch)));
      this._dotWindow = Math.min(SimpleCarousel.DOT_WINDOW, fit);
      this.dotsContainer.classList.add('simple-carousel__dots--window');
      this.dotsContainer.style.setProperty('--oa-dot-window', this._dotWindow);
      this._updateActiveDot();
      return;
    }
    if (wrapped) {
      this.dotsContainer.innerHTML = '';
      const track = document.createElement('div');
      track.className = 'simple-carousel__progress';
      const thumb = document.createElement('div');
      thumb.className = 'simple-carousel__progress-thumb';
      track.appendChild(thumb);
      this.dotsContainer.appendChild(track);
      this._progressThumb = thumb;
      this._initProgressDrag(track);
    }
    this._updateActiveDot();
  }

  _initProgressDrag(track) {
    // Make the progress track navigable: tapping jumps to that position and
    // dragging scrubs continuously through the products. We map the pointer's
    // x within the track to a fraction 0..1, then scroll the carousel to the
    // matching slide in the middle (loop) copy. On release we settle on the
    // nearest product with a smooth scroll.
    const fracFromEvent = (e) => {
      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      return Math.min(1, Math.max(0, x / Math.max(1, rect.width)));
    };
    const scrubTo = (frac) => {
      const n = this.originalCount;
      // /n cyclic model - consistent with the swipe-driven thumb (_updateActiveDot)
      // so the thumb doesn't jump when the finger lifts. Maps bar fraction -> a
      // continuous position in the middle copy's product cycle.
      const targetSlide = this._middleCopy * n + frac * n;
      this.track.scrollLeft = targetSlide * this.slideOffset - this.restOffset;
      if (this._progressThumb) {
        this._progressThumb.style.left = (frac * 100) + '%';
        this._progressThumb.style.transform = 'translateX(' + (-frac * 100) + '%)';
      }
    };
    let active = false;
    const onDown = (e) => {
      active = true;
      this.isDragging = true; // suppress loop-reset while scrubbing
      this._scrubbing = true; // tell _updateActiveDot to leave the thumb to us
      if (this._inertiaRAF) {
        cancelAnimationFrame(this._inertiaRAF);
        this._inertiaRAF = null;
      }
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
      track.classList.add('is-scrubbing');
      e.preventDefault();
      scrubTo(fracFromEvent(e));
    };
    const onMove = (e) => {
      if (!active) return;
      e.preventDefault();
      scrubTo(fracFromEvent(e));
    };
    const onUp = (e) => {
      if (!active) return;
      active = false;
      this.isDragging = false;
      this._scrubbing = false; // resume discrete thumb updates (settle to product)
      track.classList.remove('is-scrubbing');
      try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      // Settle on the nearest product (within the middle copy) with a smooth
      // scroll. /n model + clamp keeps it inside the middle copy so the smooth
      // scroll doesn't cross a seam and trigger a re-anchor mid-animation.
      const n = this.originalCount;
      const product = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * n)));
      const targetSlide = this._middleCopy * n + product;
      this._animateTo(targetSlide * this.slideOffset - this.restOffset);
    };
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
  }

  _initDotsDrag() {
    // Make the dash row (laptop view) grabbable: dragging horizontally across the
    // dashes scrubs the carousel, and the active (black) dash follows. A plain
    // click still navigates to that dash (we only suppress the click if a real
    // drag happened). Bound ONCE to the persistent dots container; it bows out
    // when the container is in progress-bar mode (that has its own drag).
    const container = this.dotsContainer;
    let active = false;
    let moved = false;
    let startX = 0;
    // Generous threshold so a normal click/tap (which often jitters a few px,
    // especially on a trackpad) still counts as a click and navigates, rather
    // than being mistaken for a drag and having its click suppressed.
    const THRESH = 8;

    const fracFromEvent = (e) => {
      const ds = container.querySelectorAll('.simple-carousel__dot');
      if (ds.length < 2) return 0;
      let left, right;
      if (this._dotWindow) {
        // Window mode: most dashes are clipped, so scrub across the visible
        // row (content box) and map it onto ALL products.
        const r = container.getBoundingClientRect(), cs = getComputedStyle(container);
        left = r.left + parseFloat(cs.paddingLeft);
        right = r.right - parseFloat(cs.paddingRight);
      } else {
        left = ds[0].getBoundingClientRect().left;
        right = ds[ds.length - 1].getBoundingClientRect().right;
      }
      return Math.min(1, Math.max(0, (e.clientX - left) / Math.max(1, right - left)));
    };
    const scrubTo = (frac) => {
      const n = this.originalCount;
      // Map the fraction across the dash row to a continuous product position in
      // the middle copy (dash 0 -> product 0 ... last dash -> product n-1).
      const product = frac * (n - 1);
      this.track.scrollLeft = (this._middleCopy * n + product) * this.slideOffset - this.restOffset;
    };

    const onMove = (e) => {
      if (!active) return;
      if (!moved && Math.abs(e.clientX - startX) > THRESH) {
        moved = true;
        this.isDragging = true;   // only NOW is it a real drag (a click never sets this)
        this._cancelInertia();
        container.classList.add('is-scrubbing');
      }
      if (moved) {
        e.preventDefault();
        scrubTo(fracFromEvent(e));
      }
    };
    const onUp = (e) => {
      if (!active) return;
      active = false;
      this.isDragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (moved) {
        container.classList.remove('is-scrubbing');
        // Settle on the nearest product (stay in the middle copy) with a smooth scroll.
        const n = this.originalCount;
        const p = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * (n - 1))));
        this._animateTo((this._middleCopy * n + p) * this.slideOffset - this.restOffset);
      }
    };
    const onDown = (e) => {
      if (this._progressThumb) return;          // progress-bar mode: it handles its own drag
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      active = true;
      moved = false;
      startX = e.clientX;
      // NOTE: isDragging / cancelInertia are set only once a real drag starts
      // (in onMove), so a plain click never flags a drag and its navigation fires.
      // Window-level listeners (no pointer capture) so the drag keeps working when
      // the cursor leaves the dash row, without retargeting a plain click away
      // from the dash (which would break click-to-navigate).
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

    // Der Hover-Zustand muss DIESELBE Karte lesen wie der Klick.
    // Die Striche sind 2px hoch: getroffen wird fast immer der Container um sie
    // herum, weshalb der Klick-Handler unten aus der x-Position navigiert statt
    // aus dem Treffer. Mit reinem CSS :hover leuchtete dagegen nur der Strich
    // selbst -- man stand im klickbaren Bereich, sah aber nichts, obwohl ein
    // Klick sehr wohl etwas getan haette. Also dieselbe Zuordnung fuer beides.
    let hoverRAF = null;
    let hoverEvent = null;
    const clearHover = () => {
      const prev = container.querySelector('.simple-carousel__dot.is-hovered');
      if (prev) prev.classList.remove('is-hovered');
    };
    const applyHover = () => {
      hoverRAF = null;
      const e = hoverEvent;
      if (!e) return;
      const ds = container.querySelectorAll('.simple-carousel__dot');
      if (!ds.length) return;
      const n = this.originalCount;
      const dash = e.target.closest ? e.target.closest('.simple-carousel__dot') : null;
      const i = dash && dash.dataset.index != null
        ? parseInt(dash.dataset.index, 10)
        : Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * (n - 1))));
      ds.forEach((d, k) => d.classList.toggle('is-hovered', k === i));
    };
    container.addEventListener('pointermove', (e) => {
      if (this._progressThumb) return;        // Fortschrittsbalken hat keine Striche
      if (e.pointerType === 'touch') return;  // sonst klebt der Zustand nach dem Tippen fest
      hoverEvent = e;
      // Auf einen Frame gebuendelt: die Zuordnung misst zwei Rechtecke, und eine
      // Maus feuert mehrmals pro Frame. Waehrend der Drift laeuft, soll das nicht
      // unnoetig Layout anfassen.
      if (hoverRAF === null) hoverRAF = requestAnimationFrame(applyHover);
    }, { passive: true });
    container.addEventListener('pointerleave', clearHover);
    container.addEventListener('pointercancel', clearHover);

    container.addEventListener('pointerdown', onDown);
    // Handle clicks for the WHOLE dot row (capture phase). The dashes are only
    // ~2px tall, so a real click almost always lands on the container around them
    // rather than precisely on a dash - that's why clicking appeared to do
    // nothing (the click target was the container, no dash handler ran). Navigate
    // from the click here: use the clicked dash's index if one was actually hit,
    // otherwise the nearest product from the click x. A click that was really a
    // drag is suppressed.
    container.addEventListener('click', (e) => {
      if (this._progressThumb) return;   // progress-bar mode handles its own taps
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
        return;
      }
      const n = this.originalCount;
      const dash = e.target.closest ? e.target.closest('.simple-carousel__dot') : null;
      let product;
      if (dash && dash.dataset.index != null) {
        product = parseInt(dash.dataset.index, 10);
      } else {
        product = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * (n - 1))));
      }
      e.stopPropagation();               // don't let the dash's own handler double-fire
      this._goToDot(product);
    }, true);
  }

  _scrollPos() {
    // The one place that answers "where are we". Normally that is the track's
    // scroll position — but while the drift is running, part of the position
    // sits in the wrapper's translation instead, and _pos carries the total.
    // Everything that reports a position (slide index, dots, progress thumb)
    // reads this, so the indicators keep working either way.
    return this._paintOn ? this._pos : this.track.scrollLeft;
  }

  _initDriftLayer() {
    // The drift translates the cards instead of scrolling them (see "Composited
    // drift" below). A transform on the track itself would drag the track's own
    // clipping edge along with it, so the cards get a wrapper INSIDE the track:
    // the wrapper moves, the track clips exactly as before — and, the whole
    // point, the track stays a normal scroll container the entire time. A
    // trackpad swipe therefore always finds something scrollable under the
    // cursor, which is what the previous version had to give up transform mode
    // for the moment the pointer arrived.
    //
    // Built only for carousels that actually drift, so every other one keeps the
    // exact DOM it always had. Layout-neutral: a nowrap flex row exactly as wide
    // as the track's content box — the same box the cards' percentage widths
    // already resolved against — inheriting the column gap so the media queries
    // that change it keep working. The cards overflow the wrapper to the right
    // and that overflow feeds the track's scroll width just as it always did.
    //
    // Runs BEFORE the first measure and position, while scrollLeft is still 0:
    // re-parenting children resets it.
    if (this.dataset.autoscroll !== 'true' || !this.isLoop) return;
    const inner = document.createElement('div');
    inner.className = 'simple-carousel__inner';
    inner.style.display = 'flex';
    inner.style.flexWrap = 'nowrap';
    inner.style.flex = '0 0 auto';
    inner.style.width = '100%';
    inner.style.columnGap = 'inherit';
    inner.style.alignItems = 'inherit';
    while (this.track.firstChild) inner.appendChild(this.track.firstChild);
    this.track.appendChild(inner);
    this._inner = inner;
    this._paintOn = false;
    this._pos = 0;
    this._base = 0;
  }

  _currentSlideIndex() {
    // Add peekOffset so the snap position (scrollLeft = N*offset -
    // peekOffset) maps cleanly back to slide N.
    return Math.round((this._scrollPos() + this.restOffset) / this.slideOffset);
  }

  _logicalIndex() {
    const raw = this._currentSlideIndex();
    const n = this.originalCount;
    return ((raw % n) + n) % n;
  }

  // Public: one product back (-1) or forward (+1) — used by the round arrow
  // buttons of the featured-collection "header nav" variant. Steps locally
  // from the current slide (no jump across the loop copies; the loop reset
  // re-centres afterwards). The drift is folded into the scroll position
  // first and held for a moment so it doesn't fight the animation.
  step(dir) {
    if (!this._oaDone) this._oaInitNow();
    if (!this.track || !this.slideOffset) return;
    this._release();
    if (this._autoHold) {
      this._autoHold.add('arrow');
      this._autoSync();
      clearTimeout(this._arrowHoldT);
      this._arrowHoldT = setTimeout(() => {
        this._autoHold.delete('arrow');
        this._autoSync();
      }, 2500);
    }
    const target = this._currentSlideIndex() + (dir < 0 ? -1 : 1);
    this._animateTo(target * this.slideOffset - this.restOffset);
  }

  _goToDot(targetLogicalIndex) {
    // Stay in the user's current copy so dot clicks animate locally;
    // the loop-reset logic pulls us back to the middle copy after
    // scroll settles if we drift to an outer copy.
    const currentSlide = this._currentSlideIndex();
    const n = this.originalCount;
    const copyStart = Math.floor(currentSlide / n) * n;
    const targetSlide = copyStart + targetLogicalIndex;
    this._animateTo(targetSlide * this.slideOffset - this.restOffset);
  }

  _updateActiveDot() {
    // Progress-track mode: position the thumb from the EXACT (continuous) scroll
    // position so it tracks the carousel perfectly in parallel - no per-product
    // stepping and no transition lag (the CSS has no transition on left/transform).
    // frac runs 0..1 across one product cycle and snaps back at the seam (instant,
    // since there's no transition to animate the 1 -> 0 jump).
    if (this._progressThumb) {
      const n = this.originalCount;
      const raw = (this._scrollPos() + this.restOffset) / this.slideOffset;
      // Position RELATIVE to the middle copy's first product. The infinite-loop
      // re-anchor keeps this within ~[0, n), so we deliberately do NOT use a
      // global "raw % n": that put the cycle's 1 -> 0 discontinuity exactly on
      // the first product, and sub-pixel rounding then flipped the thumb to the
      // far right edge on page load. Relative + clamp puts the seam at the wrap
      // (handled by the re-anchor) and pins the first product to frac 0 (left).
      let frac = (raw - this._middleCopy * n) / n;
      if (frac < 0) frac = 0; else if (frac > 1) frac = 1;
      this._progressThumb.style.left = (frac * 100) + '%';
      this._progressThumb.style.transform = 'translateX(' + (-frac * 100) + '%)';
      return;
    }
    const dots = this.dotsContainer.querySelectorAll('.simple-carousel__dot');
    if (!dots.length) return;
    const current = this._logicalIndex();
    // Only touch the DOM when the active product actually changed — this now
    // runs on every drift frame, and rewriting a class on a dozen dashes sixty
    // times a second is style work for nothing.
    if (current === this._lastDotIndex) return;
    this._lastDotIndex = current;
    dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    if (this._dotWindow) {
      const w = this._dotWindow, n = dots.length;
      const shift = Math.max(0, Math.min(n - w, current - Math.floor(w / 2)));
      this.dotsContainer.style.setProperty('--oa-dot-shift', shift);
      this.dotsContainer.setAttribute('data-oa-edge', (shift > 0 ? 'l' : '') + (shift < n - w ? 'r' : ''));
    }
  }

  _initAutoScroll() {
    // Slow continuous drift, opt-in per section (data-autoscroll="true").
    //
    // Deliberately narrow in scope:
    //   * Only with data-loop — without the looping copies the track would run
    //     into its end and just sit there.
    //   * Desktop with a real pointer only. A moving row is a moving target,
    //     and the pause-on-hover that makes it usable does not exist on touch.
    //     It also keeps every card of a long track permanently active, which is
    //     what exhausted mobile memory before (see oa-swipe-placeholder.js).
    //   * Off when the visitor asked for reduced motion.
    //   * Off while the carousel is out of view or the tab is hidden — no
    //     reason to burn a phone or laptop battery on something nobody sees.
    //   * Off while the visitor is hovering, focusing, dragging, or while our
    //     own inertia/animation is running, so it never fights an interaction.
    //
    // Speed is per second: at ~10px/s a card takes roughly half a minute to
    // pass. Anything faster reads as an ad banner rather than a shop window.
    if (this.dataset.autoscroll !== 'true' || !this.isLoop) return;

    const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    // Drift on touch screens too (every drifting carousel; a section can opt
    // out with data-autoscroll-mobile="false"). There is no hover to pause on, so a touch holds it ('touched', an
    // instant hold) and it only rolls in again 1.5s after the finger is off
    // and the swipe's momentum has stopped - nothing writes to the carousel
    // while the visitor steers it.
    const touchToo = this.dataset.autoscrollMobile !== 'false'; // on everywhere unless a section opts out
    const deviceOk = () => desktop.matches || touchToo;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    const speed = parseFloat(this.dataset.autoscrollSpeed);
    // Direction is named after the CARDS, not the scroll position — "right"
    // means the cards travel rightwards, which is a DECREASING scrollLeft.
    // Saying it the other way round in the theme editor would be a riddle.
    const backwards = this.dataset.autoscrollDirection === 'right';
    this._autoPxPerMs = (Number.isFinite(speed) && speed > 0 ? speed : 10) / 1000;
    if (backwards) this._autoPxPerMs = -this._autoPxPerMs;

    // Every reason to hold still lives in this set; the drift runs only while
    // it is empty. A set instead of a boolean because the reasons overlap —
    // leaving the carousel with the mouse must not restart it if it is also
    // scrolled out of view.
    this._autoHold = new Set();
    this._autoEase = 0;   // current speed factor, 0…1
    this._autoTarget = 0; // where it is heading
    if (!this._inner) return; // no drift layer, no drift (see _initDriftLayer)

    const hold = (reason, on) => {
      if (on) this._autoHold.add(reason); else this._autoHold.delete(reason);
      this._autoSync();
    };

    hold('viewport', true); // until the observer says otherwise
    hold('motion', calm.matches);
    hold('device', !deviceOk());

    // Fresh page: hold still until the page has finished loading and the main
    // thread has a quiet moment. Starting the drift while the browser is still
    // laying out the page and decoding the first photos is what made the very
    // first seconds stutter. 'boot' is an EASED hold, so the drift then rolls
    // in gently instead of jumping to speed. Hard cap 4s after load.
    if (document.readyState !== 'complete' || !SimpleCarousel._booted) {
      hold('boot', true);
      const go = () => {
        SimpleCarousel._booted = true;
        hold('boot', false);
      };
      const boot = () => {
        if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1500 });
        else setTimeout(go, 400);
      };
      if (document.readyState === 'complete') setTimeout(boot, 200);
      else window.addEventListener('load', () => setTimeout(boot, 300), { once: true });
      setTimeout(go, 8000);
    }

    const onMq = () => {
      hold('motion', calm.matches);
      hold('device', !deviceOk());
    };
    if (calm.addEventListener) {
      calm.addEventListener('change', onMq);
      desktop.addEventListener('change', onMq);
    }

    // The pointer merely ARRIVING now changes nothing but the speed target: the
    // track is a scroll container throughout, so a trackpad swipe finds
    // something scrollable under the cursor at any moment and the roll-out can
    // stay composited all the way down to zero. (The old version had to hand
    // the scroll position back right here, or macOS claimed the gesture as its
    // back/forward swipe and the whole page slid sideways — which put exactly
    // the visible part of the motion back on the rasterised path.)
    //
    // The hover pause hangs on the TRACK, i.e. the cards — not on the whole
    // element. The dots sit in a container spanning the full width, so with the
    // listener on the host the carousel also stopped when the pointer merely
    // crossed that strip, well outside the visible dashes.
    // Aiming at a card is the reason to pause; passing over the indicator is not.
    // Mouse only: a finger also "enters" on a tap (and iOS sends the
    // matching leave only when something else is tapped), which left the
    // phone drift held until a tap outside the carousel.
    this.track.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') hold('hover', true);
    });
    this.track.addEventListener('pointerleave', () => hold('hover', false));

    // Safety net for the hover hold. After a drag the pointer has often left the
    // track while the button was down, and the matching pointerleave does not
    // reliably arrive — the hold then stayed on and the drift only picked up
    // again once something else made the pointer re-enter and leave properly.
    // So: whenever the pointer moves and is demonstrably outside the track, drop
    // the hold. Costs nothing while the hold is not set.
    window.addEventListener('pointermove', (e) => {
      if (!this._autoHold.has('hover')) return;
      const r = this.track.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) hold('hover', false);
    }, { passive: true });

    // Using the indicator itself is a different matter — clicking or scrubbing a
    // dash should hold the carousel still until the pointer is released.
    this.dotsContainer.addEventListener('pointerdown', () => {
      this._lastPointer = performance.now();
      this._release();
      hold('grab', true);
    }, { capture: true });
    // Only KEYBOARD focus holds the carousel. Measured with a real mouse drag:
    // the click leaves the focus sitting on the card's link, no focusout ever
    // follows, and the drift stayed paused until the visitor clicked somewhere
    // else entirely — which is exactly the "it only starts again after I click
    // outside" symptom. :focus-visible is the browser's own answer to "was this
    // focus given by keyboard or by pointer", so we ask it rather than guessing.
    this.addEventListener('focusin', () => {
      // Focus that follows a press/drag/tap is not keyboard focus, whatever
      // :focus-visible claims after a drag - it held the drift until a click
      // somewhere else (laptop and phone alike).
      if (this._lastPointer && performance.now() - this._lastPointer < 1500) return;
      const el = document.activeElement;
      let byKeyboard = false;
      try { byKeyboard = !!(el && el.matches(':focus-visible')); } catch (_) {}
      if (!byKeyboard) return;
      this._release();
      hold('focus', true);
    });
    this.addEventListener('focusout', () => hold('focus', false));
    this.addEventListener('pointerdown', () => hold('focus', false), { capture: true });
    // A finger, mouse or wheel on the track takes over immediately: the
    // translation is folded into the scroll position first, in the capture
    // phase — before the drag logic runs — so the gesture starts from exactly
    // what is on screen and everything downstream sees a plain scrolling
    // carousel. The fold is one frame and moves nothing.
    //
    // No 'grab' hold for the wheel — the pointer is over the carousel anyway, so
    // 'hover' already holds it, and unlike a pointer a wheel has no "up" event
    // to release on.
    const takeOver = () => {
      this._autoEase = 0; // stop dead: they are steering now
      this._release();
    };
    this.track.addEventListener('pointerdown', () => {
      this._lastPointer = performance.now();
      takeOver();
      hold('grab', true);
    }, { capture: true });
    this.track.addEventListener('wheel', takeOver, { capture: true, passive: true });
    // Touch (phones, opt-in): held while a finger is on the carousel; after
    // the swipe - once the momentum has died down (no scroll for a while) -
    // it rolls in again on its own.
    let fingerDown = false;
    let resumeTimer = null;
    const settle = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        if (!fingerDown) hold('touched', false);
      }, 1500);
    };
    const fingerOn = () => {
      this._lastPointer = performance.now();
      fingerDown = true;
      clearTimeout(resumeTimer);
      hold('touched', true);
    };
    const fingerOff = () => {
      fingerDown = false;
      settle();
    };
    this.track.addEventListener('touchstart', () => {
      takeOver();
      if (touchToo) fingerOn();
    }, { capture: true, passive: true });
    if (touchToo) {
      this.dotsContainer.addEventListener('touchstart', fingerOn, { capture: true, passive: true });
      window.addEventListener('touchend', () => { if (fingerDown) fingerOff(); }, { passive: true });
      window.addEventListener('touchcancel', () => { if (fingerDown) fingerOff(); }, { passive: true });
      // Momentum still running: every scroll pushes the restart further out.
      this.track.addEventListener('scroll', () => {
        if (!fingerDown && this._autoHold.has('touched')) settle();
      }, { passive: true });
    }
    // Released again with the pointer, so leaving and re-entering behaves.
    window.addEventListener('pointerup', () => hold('grab', false));
    window.addEventListener('pointercancel', () => hold('grab', false));

    document.addEventListener('visibilitychange', () => hold('tab', document.hidden));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        hold('viewport', !entries[0].isIntersecting);
      }, { rootMargin: '0px' }).observe(this);
    } else {
      hold('viewport', false);
    }
  }

  _autoSync() {
    if (!this._autoHold) return;

    // Two kinds of hold.
    // EASED: pointing at the carousel or tabbing into it. Someone is watching,
    // so the movement rolls out and back in over a few hundred milliseconds —
    // an abrupt stop reads as a glitch, a roll-out reads as intent.
    // INSTANT: the drift has no business running at all (wrong device, reduced
    // motion, scrolled out of view, tab in the background) or the visitor has
    // grabbed the track. Nobody is watching, or they need control NOW, so there
    // is nothing to ease.
    const instant = ['device', 'motion', 'viewport', 'tab', 'grab', 'touched'];
    const hardStop = instant.some((r) => this._autoHold.has(r));
    this._autoTarget = this._autoHold.size === 0 ? 1 : 0;

    if (hardStop) {
      this._autoEase = 0;
      this._autoStop();
      return;
    }
    if (this._autoTarget === 0 && !this._autoRAF) return; // already standing still

    if (!this._autoRAF) {
      this._autoEase = this._autoEase || 0;
      this._autoLast = performance.now();
      this._autoRAF = requestAnimationFrame(this._autoStep); // _driftEnter picks up the position
    }
  }

  _autoStop() {
    if (this._autoRAF) {
      cancelAnimationFrame(this._autoRAF);
      this._autoRAF = null;
    }
    this._release();
  }

  /* ---- Composited drift -----------------------------------------------------
   *
   * WHY: the drift used to move by writing scrollLeft. A scroll offset is
   * rasterised, so at slow speeds every frame re-rendered the product photos a
   * fraction of a pixel further along — at 14px/s that is 0.47 device pixels per
   * frame on a 2x screen, never a whole one. The frame rate was a perfect 60
   * with zero dropped frames (measured), but the images shimmered. Raising the
   * speed to a whole device pixel per frame fixed the steady state and did
   * nothing for the ramps, because a ramp passes through every speed below it.
   *
   * SO: the drift translates the wrapper the cards sit in (_initDriftLayer),
   * which the compositor does without re-rasterising anything, at any speed.
   * The track keeps scrolling and clipping exactly as it always did — no mode
   * to switch into or out of, which is what the previous version had, and what
   * cost it the roll-out: it had to give the scroll container back the moment
   * the pointer arrived, so the most visible part of the motion was the one
   * part that stayed rasterised.
   *
   * BOOKKEEPING: _pos is the visible position (a float), _base is the track's
   * scroll position, and the wrapper carries the difference as a translation.
   * Neither is read back from the DOM per frame — a layout read straight after
   * a layout write forces a synchronous re-layout, sixty times a second.
   * Everything else in this file works on scrollLeft alone and only ever runs
   * with the difference folded away, so drag, inertia, dots and the loop are
   * untouched by any of this.
   * --------------------------------------------------------------------------- */

  _paint() {
    this._inner.style.transform =
      'translate3d(' + (this._base - this._pos).toFixed(3) + 'px, 0, 0)';
  }

  _driftStart() {
    if (this._paintOn || !this._inner) return;
    // Adopt whatever position the track is actually at — a dot animation, a
    // drag or a resize may have moved it since we last looked.
    this._pos = this._base = this.track.scrollLeft;
    this._inner.style.willChange = 'transform';
    this._paintOn = true;
    this._paint();
  }

  _release() {
    // Fold the translation into the scroll position and hand the carousel back
    // as a plain scrolling element. Both writes happen in one task, so the
    // browser paints them as a single state: no flash, no jump.
    if (!this._paintOn) return;
    this._paintOn = false;
    this.track.scrollLeft = Math.round(this._pos);
    this._inner.style.transform = '';
    // Keep the drift layer while the carousel is on screen: dropping and
    // re-promoting it on every hover / drag / restart made desktop Safari
    // repaint the strip each time (~300 tiles; Safari timeline 2026-11-03).
    // Only let it go when nobody can see the drift anyway.
    const h = this._autoHold;
    if (!h || h.has('viewport') || h.has('tab') || h.has('device') || h.has('motion')) {
      this._inner.style.willChange = '';
    }
    this._recenter();
  }

  _fold() {
    // Absorb the accumulated translation into scrollLeft WITHOUT moving the
    // picture: scroll forward by exactly as much as we stop translating. Keeps
    // the translation small, because a large one shrinks the track's scrollable
    // width (a transformed child contributes its transformed box) and the
    // browser would clamp scrollLeft. Runs a handful of times a minute.
    this.track.scrollLeft = Math.round(this._pos);
    this._base = this.track.scrollLeft; // read back what the browser accepted
  }

  _driftSeam() {
    // Step back by whole copies so the drift never runs out of track. The
    // content one copy back is pixel-identical, so moving BOTH the scroll
    // position and _pos by the same amount leaves the translation — and
    // therefore the picture — untouched.
    if (!this.isLoop) return;
    const n = this.originalCount;
    const idx = this._currentSlideIndex();
    const copyOfIdx = Math.floor(idx / n);
    if (copyOfIdx === this._middleCopy) return;
    const idxMid = idx - (copyOfIdx - this._middleCopy) * n;
    const offs = this._slideOffsets;
    // Per-slide delta rather than a fixed copyWidth: sub-pixel rounding makes
    // individual slide offsets vary, and the approximation showed as a tiny
    // hard cut each lap.
    const delta = (offs && offs[idxMid] !== undefined && offs[idx] !== undefined)
      ? offs[idxMid] - offs[idx]
      : (this._middleCopy - copyOfIdx) * this.copyWidth;
    const oldBase = this._base;
    this.track.scrollLeft = oldBase + delta;
    const actual = this.track.scrollLeft;
    this._pos += actual - oldBase;
    this._base = actual;
  }

  _autoStep = (now) => {
    const dt = Math.min(50, now - this._autoLast); // a backgrounded tab can hand us a huge gap
    this._autoLast = now;

    // Stand back while the visitor's own drag or a dot animation owns the
    // track — and give the scroll position back first, so they act on a normal
    // scrolling carousel.
    if (this.isDragging || this._inertiaRAF || this._scrollAnim) {
      this._release();
      this._autoRAF = requestAnimationFrame(this._autoStep);
      return;
    }

    // Ease the SPEED towards its target instead of switching it on and off.
    // Exponential approach, ~260ms time constant: fast enough that pointing at
    // a card feels like it answers, slow enough that neither the stop nor the
    // restart has a visible edge. Unlike before, the roll-out is composited
    // like the rest of the drift, so it stays smooth all the way to zero.
    const tau = 260;
    this._autoEase += (this._autoTarget - this._autoEase) * (1 - Math.exp(-dt / tau));
    if (this._autoTarget === 0 && this._autoEase < 0.002) {
      this._autoEase = 0;
      this._autoStop();
      return;
    }

    // Checked after the ease, so a frame that only exists to notice "we have
    // stopped" never promotes a layer just to drop it again.
    if (!this._paintOn) this._driftStart();

    // ONE float carries the position. That matters at these speeds: at 30px/s
    // and 120Hz a frame advances 0.25px, and `scrollLeft += 0.25` is rounded
    // away by the DOM every single time — which is why an earlier version of
    // the roll-out moved nothing at all and appeared to stop dead. A transform
    // takes the fraction as it is.
    this._pos += this._autoPxPerMs * this._autoEase * dt;

    this._driftSeam();
    if (Math.abs(this._pos - this._base) > 200) this._fold();
    this._paint();
    this._updateActiveDot();

    this._autoRAF = requestAnimationFrame(this._autoStep);
  };

  _recenter() {
    // Bring the viewport back into the MIDDLE copy by jumping whole copies. The
    // jump is exactly k*copyWidth, so the visible content is pixel-identical
    // (invisible). Returns true if it jumped. Safe to call during our OWN custom
    // inertia (next frame reads the new scrollLeft) but NOT during native
    // momentum or an active finger drag, where setting scrollLeft hitches.
    if (!this.isLoop) return false;
    const n = this.originalCount;
    const idx = this._currentSlideIndex();
    const copyOfIdx = Math.floor(idx / n);
    if (copyOfIdx === this._middleCopy) return false;
    // Jump by the EXACT pixel offset between the current near-slide and its
    // equivalent product in the middle copy, so that product lands precisely
    // where it is now. A fixed copyWidth would be up to 1px off for some
    // products (slide offsets vary 268/269 due to sub-pixel rounding), which
    // showed as a tiny hard cut each loop; this per-slide delta is pixel-exact.
    const idxMid = idx - (copyOfIdx - this._middleCopy) * n;
    const offs = this._slideOffsets;
    if (offs && offs[idxMid] !== undefined && offs[idx] !== undefined) {
      this.track.scrollLeft += offs[idxMid] - offs[idx];
    } else {
      this.track.scrollLeft += (this._middleCopy - copyOfIdx) * this.copyWidth;
    }
    return true;
  }

  _cancelInertia() {
    if (this._inertiaRAF) {
      cancelAnimationFrame(this._inertiaRAF);
      this._inertiaRAF = null;
    }
    if (this._scrollAnim) {
      cancelAnimationFrame(this._scrollAnim);
      this._scrollAnim = null;
    }
  }

  _animateTo(target) {
    // Smoothly scroll to an absolute scrollLeft by writing it every frame (rAF).
    // We deliberately do NOT use the native scrollTo({behavior:'smooth'}): on this
    // overflow-x container it's unreliable on some devices (the click navigation
    // appeared to "do nothing"), whereas direct per-frame scrollLeft writes work
    // (same mechanism as the inertia glide).
    this._cancelInertia();
    const start = this.track.scrollLeft;
    const dist = target - start;
    if (Math.abs(dist) < 1) { this.track.scrollLeft = target; return; }
    const dur = 340;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / dur);
      this.track.scrollLeft = start + dist * ease(t);
      if (t < 1) {
        this._scrollAnim = requestAnimationFrame(step);
      } else {
        this._scrollAnim = null;
      }
    };
    this._scrollAnim = requestAnimationFrame(step);
  }

  _loopManage() {
    // Seamless infinite loop across the seam WITHOUT a visible stop. The problem:
    // on many devices, setting scrollLeft mid-fling cancels the native momentum,
    // so re-anchoring during native momentum stops the carousel dead at the seam
    // (it stopped at the first product). Instead, the moment native momentum
    // carries us out of the middle copy, we capture its velocity, re-anchor
    // (which cancels the native fling - that's fine) and continue the glide with
    // our OWN inertia, which can re-anchor freely. Net effect: the motion runs
    // continuously across the loop point.
    if (!this.isLoop) return;
    if (this.isDragging || this._touchActive || this._inertiaRAF) return;
    const n = this.originalCount;
    const copyOfIdx = Math.floor(this._currentSlideIndex() / n);
    if (copyOfIdx === this._middleCopy) return;
    // Hands off while the idle drift owns the carousel: it handles its own seam
    // (_driftSeam) and carries its own motion. The inertia hand-off below is for
    // a FINGER FLING under native momentum — applying it to the drift started a
    // decelerating glide on every lap, which was the lurch at the seam.
    if (this._autoRAF) return;
    if (this._wheelActive) {
      // Trackpad / mouse-wheel scrolling: just re-anchor seamlessly (a jump by
      // one pixel-identical copy). Do NOT hand off to our own inertia here -
      // while the wheel is still driving the native scroll, the inertia fights
      // it and produced a recurring hitch at the loop seam each time around.
      this._recenter();
      return;
    }
    // Native touch momentum (no wheel). Leaving the middle copy is NOT a reason
    // to act: there is a whole pixel-identical copy on either side of it, and
    // _maybeLoopReset brings us back invisibly the moment the glide settles.
    //
    // Stepping in here used to happen on practically every swipe — the carousel
    // rests at the START of the middle copy, so any swipe back to the previous
    // products crossed the seam at once. Writing scrollLeft mid-fling kills the
    // browser's own momentum (which runs off the main thread) and replaced it
    // with a JS glide on the main thread that also braked harder: the hitch you
    // felt on the phone. Now we only take over when the fling would actually
    // run into the end of the track, which with three copies is rare.
    const pos = this.track.scrollLeft;
    const margin = Math.max(600, this.track.clientWidth * 1.5);
    const max = this._maxScroll || (this.track.scrollWidth - this.track.clientWidth);
    if (pos > margin && pos < max - margin) return;
    const v = this._scrollVelocity || 0;
    this._recenter();
    // Continue with roughly the browser's own deceleration (iOS: 0.998/ms), so
    // even this rare hand-off does not visibly change the glide's pace.
    if (Math.abs(v) > 0.02) this._startInertia(v, 0.002);
  }

  _maybeLoopReset() {
    // After-stop safety net: re-center only when nothing is actively driving the
    // scroll, so we never fight native momentum or a finger drag. Continuous
    // motion is kept seamless by the inertia hand-off in _loopManage.
    if (this.isDragging || this._touchActive || this._inertiaRAF) return;
    // The drift keeps its own books (_driftSeam) and owns the scroll position
    // while it paints; re-anchoring from here would move _base out from under
    // the translation and shift the picture. Its own folds are what triggered
    // this timer in the first place.
    if (this._paintOn) return;
    // Jump only when the track is actually running short, not every time a
    // swipe comes to rest outside the middle copy.
    //
    // Every jump is a programmatic scroll, and on the phone it cannot be
    // perfectly exact: after a fling the browser's own scroll position rests on
    // a fraction of a pixel (1/3px steps on a 3x iPhone), but scrollLeft is
    // read and written in whole pixels there, so the jump drops that fraction —
    // the occasional one-device-pixel twitch, in either direction, right as a
    // swipe ends. With the carousel resting at the start of the middle copy,
    // the very first swipe back triggered one. Now there must be less than
    // (fling hand-off zone + 2000px) left before we move anything: with 25
    // products that is ~10 cards of browsing in one direction, and a fling
    // still cannot run into the hand-off zone from there without real force.
    // Small carousels (one copy shorter than that) re-anchor as before, and so
    // does the progress-bar indicator, whose maths assume the middle copy.
    if (!this._progressThumb) {
      const pos = this.track.scrollLeft;
      const max = this._maxScroll || (this.track.scrollWidth - this.track.clientWidth);
      // Capped at the room the middle copy has to either end (one copy with
      // three copies, two with five, …), so a re-anchor always lands outside
      // the zone that would trigger it again.
      const keep = Math.min(this._middleCopy * this.copyWidth, Math.max(600, this.track.clientWidth * 1.5) + 2000);
      if (Math.min(pos, max - pos) >= keep) return;
    }
    this._recenter();
  }

  _initScroll() {
    let rafId = null;
    let scrollEndTimer = null;
    const hasScrollEnd = 'onscrollend' in window;
    this._scrollVelocity = 0;
    this._touchActive = false;
    let lastLeft = this.track.scrollLeft;
    let lastTime = performance.now();

    this.track.addEventListener('scroll', () => {
      const now = performance.now();
      this._lastScrollTime = now;
      const dt = now - lastTime;
      if (dt > 0) {
        this._scrollVelocity = (this.track.scrollLeft - lastLeft) / dt; // px/ms
        lastLeft = this.track.scrollLeft;
        lastTime = now;
      }
      // Always clear: this also cancels touchend's check once momentum follows.
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = hasScrollEnd ? null // the browser tells us when it has settled
        : setTimeout(() => this._maybeLoopReset(), 200);
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        this._updateActiveDot();
        this._loopManage();
        rafId = null;
      });
    });
    // "Settled" was guessed from 120ms without a scroll event. The slow tail of
    // a phone fling can go quiet for that long while still creeping, and the
    // re-anchor then stopped it a few pixels early — a small jolt at the very
    // end. Where the browser has a real scrollend event (it fires only once
    // the finger is up AND the momentum is over) we use that instead; elsewhere
    // the timer waits a little longer.
    if (hasScrollEnd) {
      this.track.addEventListener('scrollend', () => this._maybeLoopReset());
    }

    // Don't reposition while a finger is actively driving the native scroll -
    // setting scrollLeft then fights the browser and hitches. We only re-anchor
    // once the finger is up (native momentum -> inertia hand-off) or after the
    // scroll fully settles.
    this.track.addEventListener('touchstart', () => {
      this._touchActive = true;
      this._cancelInertia();
    }, { passive: true });
    this.track.addEventListener('touchend', () => {
      this._touchActive = false;
      // Re-arm the settle check. A finger that stops a glide and lifts without
      // moving produces no further scroll event, so the timer above (which
      // stood back while the finger was down) would never re-centre — and a
      // few such flicks in a row walked the track towards its physical end.
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => this._maybeLoopReset(), 200);
      // Let the browser's OWN momentum scrolling carry the fling. It runs on the
      // compositor thread and is far smoother than a main-thread JS glide (which
      // rewrites scrollLeft every frame and stutters whenever the main thread is
      // busy - that main-thread glide was the "ruckelig" swipe on mobile). We
      // deliberately no longer take over with our own inertia at lift-off.
      //
      // The seamless loop stays intact: while native momentum runs, the scroll
      // handler calls _loopManage, which re-anchors invisibly and - only if a
      // fling actually carries PAST the seam - captures the live velocity and
      // continues just that remainder with our inertia. _maybeLoopReset re-centers
      // once the scroll fully settles. So the common case (a swipe that stays
      // inside the middle copy) is pure, buttery native momentum with no jank.
    }, { passive: true });
    this.track.addEventListener('touchcancel', () => { this._touchActive = false; }, { passive: true });

    // Track trackpad / mouse-wheel scrolling. While wheeling, the loop is
    // re-anchored seamlessly WITHOUT our inertia hand-off (see _loopManage) -
    // the hand-off fought the native wheel scroll and hitched at the seam each
    // loop. The flag clears shortly after the last wheel event.
    let wheelTimer = null;
    this.track.addEventListener('wheel', () => {
      this._wheelActive = true;
      this._cancelInertia();
      if (wheelTimer) clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => { this._wheelActive = false; }, 160);
    }, { passive: true });
  }

  _initResize() {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      // Only a change of WIDTH moves anything here. A height change (a font
      // arriving, a card's text re-wrapping, a lazy image settling) used to run
      // the whole block too — and _positionToMiddle then snapped the carousel
      // back to its first product, in the middle of a swipe if need be.
      if (this.track.clientWidth === this._measuredW) return;
      // Fold the translation away first — _measure/_positionToMiddle work on
      // real scroll positions and on untransformed geometry. The drift picks
      // itself up again next frame.
      this._release();
      // Re-measure and re-pin to the middle copy on every resize tick. Pinning
      // immediately (rather than debounced) keeps the carousel visually anchored
      // while the cards resize - debouncing let the view drift natively, which
      // looked like the products auto-scrolling/swiping past during a resize.
      this._measure();
      this._positionToMiddle();
      this._renderDots();
    });
    ro.observe(this.track);
  }

  _initDrag() {
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let samples = []; // recent {time, scrollLeft} pairs for velocity
    let pendingLeft = null; // latest target scrollLeft awaiting a frame
    let moveRAF = null;
    const DRAG_THRESHOLD = 5;
    const VELOCITY_WINDOW_MS = 100;
    this.isDragging = false;
    this._inertiaRAF = null;

    // Apply the most recent drag position once per animation frame. High-Hz
    // mice fire pointermove several times per frame; writing scrollLeft on
    // every event forces redundant scroll work and makes the drag feel
    // choppy. Coalescing to one write per frame keeps it smooth.
    const applyMove = () => {
      moveRAF = null;
      if (pendingLeft === null) return;
      this.track.scrollLeft = pendingLeft;
      const now = performance.now();
      samples.push({ time: now, scrollLeft: this.track.scrollLeft });
      while (samples.length > 2 && samples[0].time < now - VELOCITY_WINDOW_MS) {
        samples.shift();
      }
    };

    const cancelInertia = () => {
      if (this._inertiaRAF) {
        cancelAnimationFrame(this._inertiaRAF);
        this._inertiaRAF = null;
      }
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      // Any new grab cancels an ongoing inertia glide so the user can
      // catch the carousel mid-flight.
      cancelInertia();
      isDown = true;
      moved = false;
      this.isDragging = true;
      startX = e.clientX;
      startScrollLeft = this.track.scrollLeft;
      samples = [{ time: performance.now(), scrollLeft: this.track.scrollLeft }];
      // NOTE: we do NOT add the 'is-dragging' class here. That class sets the
      // product links to pointer-events:none; doing it on pointerdown meant a
      // plain click landed on the card wrapper instead of the link, so clicks
      // never navigated. It is added in onPointerMove once a real drag starts.
      // IMPORTANT: do NOT use setPointerCapture here. Pointer capture
      // retargets the subsequent `click` event to the track instead of the
      // product link, which made the carousel cards unclickable. Instead we
      // track the drag with window-level listeners (added now, removed in
      // finishDrag) so the drag keeps working even when the cursor leaves
      // the track, without hijacking the click.
      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', finishDrag);
      window.addEventListener('pointercancel', finishDrag);
    };

    const onPointerMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        // Only now that it's a real drag do we flag the track as dragging
        // (which disables link clicks + shows the grabbing cursor). A plain
        // click never reaches this, so its link stays clickable.
        this.track.classList.add('is-dragging');
      }
      if (moved) {
        // preventDefault must run in the event (not the rAF) to stop the
        // native text-selection / scroll; the actual scrollLeft write is
        // coalesced into applyMove so it happens at most once per frame.
        e.preventDefault();
        pendingLeft = startScrollLeft - dx;
        if (moveRAF === null) moveRAF = requestAnimationFrame(applyMove);
      }
    };

    const finishDrag = (e) => {
      if (!isDown) return;
      // Flush any drag position still waiting for a frame so the release
      // velocity is computed from the true final position.
      if (moveRAF !== null) {
        cancelAnimationFrame(moveRAF);
        moveRAF = null;
      }
      if (pendingLeft !== null) {
        this.track.scrollLeft = pendingLeft;
        pendingLeft = null;
      }
      isDown = false;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      // Detach the window listeners that were active for this drag.
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
      // Smoother release: continue the scroll with a decaying velocity
      // so the carousel glides for a short moment instead of stopping
      // dead at the cursor position. The dots are still the only thing
      // that snaps to a slide boundary.
      if (moved && samples.length >= 2) {
        const last = samples[samples.length - 1];
        const first = samples[0];
        const dt = last.time - first.time;
        if (dt > 0) {
          const velocity = (last.scrollLeft - first.scrollLeft) / dt; // px per ms
          if (Math.abs(velocity) > 0.05) this._startInertia(velocity);
        }
      }
    };

    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    this.track.addEventListener('pointerdown', onPointerDown);
    // pointermove / pointerup / pointercancel are bound on window only while
    // a drag is active (see onPointerDown / finishDrag) so the cursor can
    // leave the track mid-drag without us needing pointer capture.
    this.track.addEventListener('click', onClickCapture, true);
    // Suppress the browser's native image/link ghost-drag inside the track,
    // which would otherwise interrupt a mouse drag.
    this.track.addEventListener('dragstart', (e) => e.preventDefault());

    // A dot click should also kill any ongoing inertia, otherwise the
    // smooth scrollTo from _goToDot fights the glide.
    this.dotsContainer.addEventListener('pointerdown', cancelInertia, true);
  }

  _startInertia(initialVelocity, friction) {
    // Velocity unit: pixels per millisecond. We drive an ABSOLUTE position and
    // write it every frame so this inertia is authoritative: it overrides any
    // residual native momentum (no double-speed glide) and lets us re-anchor the
    // infinite loop seamlessly mid-glide - a scrollLeft jump by a whole copy is
    // invisible, and the glide simply continues from the new position.
    this._cancelInertia();
    let velocity = initialVelocity;
    const minSpeed = 0.02;          // ~20 px/s cutoff
    // ~e^-0.0045/ms settles a mouse fling in ~600ms; the touch hand-off in
    // _loopManage passes the gentler native value instead.
    const frictionPerMs = friction || 0.0045;
    let lastTime = performance.now();
    let pos = this.track.scrollLeft;

    const step = () => {
      const now = performance.now();
      const dt = now - lastTime;
      lastTime = now;
      pos += velocity * dt;
      this.track.scrollLeft = pos;
      // Seamless infinite-loop re-anchor during our own glide. If it jumped,
      // adopt the new (pixel-identical) position so the glide continues smoothly.
      if (this._recenter()) pos = this.track.scrollLeft;
      // Exponential decay: velocity *= exp(-friction * dt)
      velocity *= Math.exp(-frictionPerMs * dt);
      if (Math.abs(velocity) < minSpeed) {
        this._inertiaRAF = null;
        return;
      }
      this._inertiaRAF = requestAnimationFrame(step);
    };
    this._inertiaRAF = requestAnimationFrame(step);
  }
}

if (!customElements.get('simple-carousel')) {
  // Max dashes visible in the sliding-window indicator (phones).
SimpleCarousel.DOT_WINDOW = 9;

customElements.define('simple-carousel', SimpleCarousel);
}
}
